import { useEffect, useRef } from 'react';
import './OtpInput.css';

interface Props {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly length?: number;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  readonly label?: string;
}

const DIGIT = /^\d$/;

// 6-digit code input with auto-advance on type and back-step on Backspace.
// Used by /auth/verify, /auth/mfa-challenge, /auth/forgot-password (step 2).
export default function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus,
  label,
}: Props) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(length, ' ').slice(0, length).split('');

  useEffect(() => {
    if (autoFocus) {
      inputs.current[0]?.focus();
    }
  }, [autoFocus]);

  function setDigit(i: number, ch: string) {
    const next = [...digits];
    next[i] = ch;
    const joined = next.join('').trimEnd();
    onChange(joined);
  }

  function handleKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i] && digits[i] !== ' ') {
        setDigit(i, ' ');
        inputs.current[i]?.focus();
      } else if (i > 0) {
        setDigit(i - 1, ' ');
        inputs.current[i - 1]?.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      inputs.current[i - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      inputs.current[i + 1]?.focus();
      return;
    }
    if (DIGIT.test(e.key)) {
      e.preventDefault();
      setDigit(i, e.key);
      if (i < length - 1) {
        inputs.current[i + 1]?.focus();
      }
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted.length === 0) {
      return;
    }
    e.preventDefault();
    onChange(pasted);
    const focusAt = Math.min(pasted.length, length - 1);
    inputs.current[focusAt]?.focus();
  }

  return (
    <div className="om-otp">
      {label ? <span className="om-otp__label">{label}</span> : null}
      <div className="om-otp__row" role="group" aria-label={label ?? 'One-time code'}>
        {Array.from({ length }, (_unused, i) => {
          const ch = digits[i];
          return (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              className="om-otp__cell"
              value={ch && ch !== ' ' ? ch : ''}
              onChange={() => {
                /* handled in handleKey */
              }}
              onKeyDown={(e) => handleKey(i, e)}
              onPaste={handlePaste}
              disabled={disabled}
              aria-label={`Digit ${i + 1}`}
            />
          );
        })}
      </div>
    </div>
  );
}
